import React, { useContext, useEffect, useState } from "react";
import classNames from "classnames";
import { NextSeo } from "next-seo";

import { LayoutContext, SCREENTYPE } from "@/hooks/useLayout";

import { getSEONoFollow, getSEONoIndex } from "@/utils/nextjs/seo";
import AccordeonCartes from "@/ui/accordeonCartes";
import { PUBFORMATS } from "@/ui/pub/formats";
import { PUB_EVERY_X, withPubEveryX } from "@/configs/pub";
import { TYPE_CONTENU } from "@/utils/enums";
import Breadcrumb from "@/ui/breadcrumb";
import Layout from "@/layout";

import { SIZE, VARIANT } from "@/ui/bouton";
import BoutonLink from "@/ui/boutonLink";
import { path } from "@/utils/path";

import styles from "./publications.module.scss";

import { Categorie } from "@/types/categorie";
import { Viewport } from "@/types/enum";
import Pub from "@/ui/pub";

type LayoutPagePublicationsProps = {
  contenu: Categorie[];
  sousContenus?: Categorie[];
  boutonTitle?: string;
  parentId?: number | null;
  sectionTitle?: string;
  title: string;
  viewport: Viewport;
};

const LayoutPagePublications = ({
  contenu,
  boutonTitle = "Voir tous",
  sectionTitle = "Tous les articles",
  parentId = null,
  title,
  viewport,
}: LayoutPagePublicationsProps) => {
  const { screenType } = useContext(LayoutContext);
  const [isMobile, setIsMobile] = useState(true);

  const categoriesWithPub = withPubEveryX(
    contenu.filter((item) => item.parentId === parentId),
    isMobile ? PUB_EVERY_X.CATEGORIES_MOBILE : PUB_EVERY_X.CATEGORIES_DESKTOP
  );

  useEffect(() => {
    setIsMobile(
      viewport == SCREENTYPE.MOBILE || screenType == SCREENTYPE.MOBILE
    );
  }, [screenType]);

  return (
    <Layout mainBody={false} viewport={viewport}>
      <NextSeo
        nofollow={getSEONoFollow()}
        noindex={getSEONoIndex()}
        title={title}
      />

      <div className={classNames(styles.me)}>
        <div className="body">
          <Breadcrumb title={title} />

          <div id="main-content" className="content">
            <h1 className={styles.sectionTitle}>{sectionTitle}</h1>
          </div>
        </div>

        {categoriesWithPub?.map((category) => {
          const { id, type, nom } = category as Categorie;

          if (type === TYPE_CONTENU.PUBLICITE) {
            return (
              <div
                key={id}
                className={styles.sectionPub}
                data-testid="publication-publicite"
              >
                <div className="body">
                  <Pub format={PUBFORMATS.leaderboardBillboard} />
                </div>
              </div>
            );
          }

          const sousCategories = contenu.filter(
            (sousCategory) => sousCategory.parentId === id
          );

          const categoryLink = path.fill(path.CATEGORY, category);

          return (
            <div key={id} className={classNames(styles.block, "body")}>
              <h2 className={styles.title}>
                <span className={styles.titleName}>{nom}</span>
                <BoutonLink
                  href={categoryLink}
                  size={isMobile ? SIZE.XS : SIZE.SM}
                  variant={VARIANT.PRIMARY}
                  className={styles.btnLink}
                >
                  {boutonTitle}
                </BoutonLink>
              </h2>
              <AccordeonCartes contenus={sousCategories} viewport={viewport} />
            </div>
          );
        })}
      </div>
    </Layout>
  );
};

export default LayoutPagePublications;
